import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'ko', label: '한국어', short: 'KO' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'vi', label: 'Tiếng Việt', short: 'VI' },
];

interface LanguageSwitcherProps {
  dropUp?: boolean;
  align?: 'left' | 'right';
}

export default function LanguageSwitcher({ dropUp = false, align = 'right' }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => l.code === i18n.language) ?? languages[1];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-silver/25 text-silver/80 hover:text-white hover:border-silver/50 text-xs font-sans font-medium tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap"
        aria-label="Select language"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-global-line text-sm" />
        </div>
        <span>{currentLang.short}</span>
        <div className="w-3 h-3 flex items-center justify-center">
          <i
            className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {open && (
        <div
          className={`absolute bg-[#0a1628] border border-silver/20 rounded-lg overflow-hidden w-36 z-[999] ${
            align === 'left' ? 'left-0' : 'right-0'
          } ${
            dropUp ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {languages.map((lang) => {
            const isActive = lang.code === i18n.language;
            return (
              <button
                key={lang.code}
                onClick={() => {
                  i18n.changeLanguage(lang.code);
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 text-sm font-sans flex items-center justify-between transition-colors duration-150 cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'text-white bg-white/10'
                    : 'text-silver/70 hover:text-white hover:bg-white/5'
                }`}
              >
                <span>{lang.label}</span>
                {isActive && (
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-xs text-silver" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
